<script setup lang="ts">
  import { ref, watch } from 'vue';
  import ArrowRightCircleIcon from '@/shared/assets/icons/ArrowRightCircleIcon.svg';
  import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';
  import { IconImage, SelectInput } from '@/shared/ui';
  import RuleCardFooter from './RuleCardFooter.vue';
  import RuleThenBlockCard from './RuleThenBlockCard.vue';
  import type { ConditionalRuleAction, ConditionalRuleTarget } from '../../model/form-rules.store';

  interface SelectOption {
    id: string;
    name: string;
  }

  interface DraftThenBlock {
    id: string;
    action: ConditionalRuleAction | '';
    targets: ConditionalRuleTarget[];
    confirmed: boolean;
  }

  interface ThenActionPayload {
    block: DraftThenBlock;
    value: string | number;
  }

  interface ThenIndexedPayload {
    block: DraftThenBlock;
    index: number;
  }

  const props = defineProps<{
    ruleName: string;
    expanded: boolean;
    triggerOption: string;
    triggerOptions: SelectOption[];
    thenBlocks: DraftThenBlock[];
    actionOptions: SelectOption[];
    activeThenBlockId?: string | null;
    addDisabled?: boolean;
    nameDisabled?: boolean;
    hasUnsavedError?: boolean;
    readonly?: boolean;
  }>();

  const emit = defineEmits<{
    (event: 'update-rule-name', value: string): void;
    (event: 'update-expanded', value: boolean): void;
    (event: 'update-trigger-option', value: string | number): void;
    (event: 'update-then-action', payload: ThenActionPayload): void;
    (event: 'add-item', block: DraftThenBlock): void;
    (event: 'delete-then', payload: ThenIndexedPayload): void;
    (event: 'confirm-then', block: DraftThenBlock): void;
    (event: 'remove-target', payload: { block: DraftThenBlock; targetId: string }): void;
    (event: 'add-condition'): void;
    (event: 'delete-rule'): void;
  }>();

  const ruleNameInput = ref(props.ruleName);
  watch(
    () => props.ruleName,
    (name) => {
      ruleNameInput.value = name;
    }
  );

  function handleRuleNameInput(event: Event) {
    if (props.readonly || props.nameDisabled || isItemSelectionLocked()) return;
    ruleNameInput.value = (event.target as HTMLInputElement).value;
  }

  function handleRuleNameChange() {
    if (props.readonly || props.nameDisabled || isItemSelectionLocked()) return;

    const trimmed = ruleNameInput.value.trim();
    if (!trimmed) {
      emit('update-rule-name', '');
      return;
    }

    ruleNameInput.value = trimmed;
    emit('update-rule-name', trimmed);
  }

  function isThenBlockActive(blockId: string) {
    return props.activeThenBlockId === blockId;
  }

  function isThenBlockBlocked(blockId: string) {
    return !!props.activeThenBlockId && !isThenBlockActive(blockId);
  }

  function isItemSelectionLocked() {
    return !!props.activeThenBlockId;
  }
</script>

<template>
  <div
    class="flex shrink-0 flex-col rounded-lg border bg-white-02 p-4"
    :class="hasUnsavedError ? 'border-[var(--theme-secondary-ink)]' : 'border-gray-02'"
    :aria-invalid="hasUnsavedError || undefined"
    data-testid="rule-creation-form"
  >
    <div class="flex items-center justify-between">
      <div class="flex min-w-0 items-center gap-2">
        <span class="flex shrink-0 items-center justify-center rounded bg-[var(--theme-secondary)] text-white">
          <IconImage :src="ArrowRightCircleIcon" alt="Regra" class="[&_path]:stroke-white" />
        </span>
        <input
          :value="ruleNameInput"
          type="text"
          aria-label="Nome da regra"
          :disabled="readonly || nameDisabled || isItemSelectionLocked()"
          class="min-w-0 flex-1 bg-transparent text-sm font-medium text-gray-07 outline-none disabled:cursor-default"
          @click.stop
          @input="handleRuleNameInput"
          @change="handleRuleNameChange"
        />
      </div>
      <button
        type="button"
        class="flex size-8 items-center justify-center rounded-lg text-gray-05 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Recolher regra"
        :aria-expanded="expanded"
        :disabled="readonly || nameDisabled || isItemSelectionLocked()"
        @click="emit('update-expanded', !expanded)"
      >
        <IconImage
          :src="ArrowDown"
          alt="Recolher regra"
          class="transition-transform"
          :class="{ 'rotate-180': expanded }"
        />
      </button>
    </div>

    <div class="mt-2 border-t border-gray-03" />

    <div
      class="mt-4 flex flex-col"
      :style="expanded ? undefined : { display: 'none' }"
      data-testid="rule-expanded-content"
    >
      <div class="flex flex-col gap-2 rounded-lg border border-gray-02 bg-white-02 p-4">
        <label class="text-base font-medium text-gray-07" for="rule-trigger-option">
          Quando selecionar:
        </label>
        <SelectInput
          id="rule-trigger-option"
          :model-value="triggerOption"
          :options="triggerOptions"
          :disabled="readonly || isItemSelectionLocked()"
          label="Escolha a opção"
          button-class="h-[44px] bg-white-01 border border-gray-02 text-sm text-gray-07 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
          max-height="220px"
          @update:model-value="emit('update-trigger-option', $event)"
        />
      </div>

      <RuleThenBlockCard
        v-for="(thenBlock, thenIndex) in thenBlocks"
        :key="thenBlock.id"
        class="mt-4"
        :action="thenBlock.action"
        :targets="thenBlock.targets"
        :action-options="actionOptions"
        :action-disabled="readonly || !triggerOption || isItemSelectionLocked()"
        :delete-disabled="
          readonly ||
          (thenBlock.targets.length === 0 && thenIndex === 0) ||
          isItemSelectionLocked() ||
          isThenBlockBlocked(thenBlock.id)
        "
        :add-disabled="readonly || !triggerOption || !thenBlock.action || isItemSelectionLocked()"
        :confirm-disabled="readonly || !isThenBlockActive(thenBlock.id)"
        :target-removal-disabled="readonly || !isThenBlockActive(thenBlock.id)"
        :selection-active="isThenBlockActive(thenBlock.id)"
        :faded="!triggerOption"
        :action-select-id="`rule-action-${thenBlock.id}`"
        @add-item="emit('add-item', thenBlock)"
        @delete="emit('delete-then', { block: thenBlock, index: thenIndex })"
        @confirm="emit('confirm-then', thenBlock)"
        @update-action="emit('update-then-action', { block: thenBlock, value: $event })"
        @remove-target="emit('remove-target', { block: thenBlock, targetId: $event })"
      />

      <RuleCardFooter
        :add-disabled="readonly || addDisabled || isItemSelectionLocked()"
        :delete-disabled="readonly || nameDisabled || isItemSelectionLocked()"
        @add-condition="emit('add-condition')"
        @delete-rule="emit('delete-rule')"
      />
    </div>
  </div>
</template>
